import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw, Home, Copy, FileText } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { captureError } from '@/lib/errorTracker';
import { ERROR_VARIANTS, pickRandom, type ErrorVariant } from './fallbackVariants';
interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
  level?: 'page' | 'section';
  componentName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorVariant: ErrorVariant;
}

/**
 * ErrorBoundary Component
 * 
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI
 * 
 * Usage:
 * <ErrorBoundary level="page">
 *   <YourComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorVariant: ERROR_VARIANTS[0]!,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error, errorVariant: pickRandom(ERROR_VARIANTS) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const componentName = this.props.componentName || 'Unknown Component';

    // Enhanced logging with component identification
    console.group(`🛡️ [ErrorBoundary] ${componentName} Failed`);
    console.error('ErrorBoundary', '💥 Error caught:', {
      message: error.message,
      component: componentName,
      stack: error.stack,
      reactStack: errorInfo.componentStack,
    });
    console.groupEnd();

    // Store error info in state
    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Self-owned error tracker (src/lib/errorTracker.ts) — writes to
    // public.error_events for authenticated sessions; feeds the daily digest.
    captureError(error, {
      source: 'error-boundary',
      meta: { componentName, componentStack: errorInfo.componentStack },
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  copyErrorToClipboard = () => {
    if (!this.state.error) return;

    const componentName = this.props.componentName || 'Unknown Component';
    const errorText = `🛡️ ErrorBoundary Report\n\nComponent: ${componentName}\nError: ${this.state.error.message}\n\nStack Trace:\n${this.state.error.stack}\n\nReact Component Stack:\n${this.state.errorInfo?.componentStack || 'N/A'}`;

    navigator.clipboard.writeText(errorText).then(() => {
      toast({
        title: '📋 Error copied to clipboard',
        description: 'You can now paste this into your issue tracker or debug notes'
      });
    }).catch(() => {
      console.log('📋 Error Report:\n', errorText);
      toast({
        title: '📋 Error logged to console',
        description: 'Check browser console for the full error report'
      });
    });
  };

  getErrorHint = (error: Error): string => {
    const message = error.message.toLowerCase();

    if (message.includes('cannot read property') && message.includes('undefined')) {
      return '💡 Hint: Check if data is loaded before rendering. Add null checks or loading states.';
    }
    if (message.includes('cannot read properties of null')) {
      return '💡 Hint: A required prop or state value is null. Verify data dependencies.';
    }
    if (message.includes('too many re-renders')) {
      return '💡 Hint: Check useEffect dependencies or state updates in render functions.';
    }
    if (message.includes('invalid hook call')) {
      return '💡 Hint: Hooks must be called at the top level. Check conditional hook usage.';
    }
    if (message.includes('failed to fetch') || message.includes('network')) {
      return '💡 Hint: API call failed. Check network connectivity and endpoint URLs.';
    }
    if (message.includes('unexpected token')) {
      return '💡 Hint: JSON parsing error. Check API response format.';
    }
    if (message.includes('hydration')) {
      return '💡 Hint: Server/client mismatch. Check for dynamic content or useEffect usage.';
    }

    return '💡 Hint: Check the component\'s props, state, and data dependencies.';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI based on level
      const isPageLevel = this.props.level === 'page';
      const showDetails = this.props.showDetails ?? (process.env.NODE_ENV === 'development');

      const componentName = this.props.componentName;
      const errorHint = this.state.error ? this.getErrorHint(this.state.error) : '';
      const variant = this.state.errorVariant;

      return (
        <Card className={`${isPageLevel ? 'w-full max-w-2xl mx-auto mt-8' : 'w-full'} border-border bg-card`}>
          <CardHeader>
            <div className="flex items-start gap-3">
              <img
                src={variant.image}
                alt=""
                className="h-14 w-14 shrink-0 rounded-xl object-cover"
                style={{ objectPosition: variant.objectPosition }}
              />
              <div className="flex-1">
                <CardTitle className="flex items-center gap-2">
                  {componentName ? `${componentName} Component Failed` : (isPageLevel ? 'Page Error' : 'Component Error')}
                </CardTitle>
                <CardDescription>
                  {this.state.error?.message || (isPageLevel
                    ? 'An unexpected error occurred while loading this page.'
                    : 'This section encountered an error and cannot be displayed.')}
                </CardDescription>
                {errorHint && (
                  <div className="mt-2 p-2 bg-[hsl(var(--chart-2)/0.15)] border border-[hsl(var(--chart-2)/0.3)] rounded text-[hsl(var(--chart-2))] text-sm">
                    {errorHint}
                  </div>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {showDetails && this.state.error && (
              <div className="rounded-lg bg-muted p-4 font-mono text-sm text-muted-foreground">
                <div className="font-semibold mb-2">Error Details:</div>
                <div className="mb-2">
                  <strong>Message:</strong> {this.state.error.message}
                </div>
                {this.state.error.stack && (
                  <details className="mt-2">
                    <summary className="cursor-pointer font-semibold">Stack Trace</summary>
                    <pre className="mt-2 text-xs overflow-x-auto whitespace-pre-wrap">
                      {this.state.error.stack}
                    </pre>
                  </details>
                )}
                {this.state.errorInfo && (
                  <details className="mt-2">
                    <summary className="cursor-pointer font-semibold">Component Stack</summary>
                    <pre className="mt-2 text-xs overflow-x-auto whitespace-pre-wrap">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={this.handleReset}
                variant="default"
                size="sm"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>

              <Button
                onClick={this.copyErrorToClipboard}
                variant="outline"
                size="sm"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy Error
              </Button>

              {isPageLevel && (
                <Button
                  onClick={this.handleGoHome}
                  variant="outline"
                  size="sm"
                >
                  <Home className="h-4 w-4 mr-2" />
                  Go Home
                </Button>
              )}

              {process.env.NODE_ENV === 'development' && (
                <Button
                  onClick={() => console.log('🐛 Full Error Object:', this.state.error, this.state.errorInfo)}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  <FileText className="h-3 w-3 mr-1" />
                  Log to Console
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      );
    }

    return this.props.children;
  }
}

/**
 * SectionErrorBoundary
 * Convenient wrapper for section-level errors
 */
export const SectionErrorBoundary = ({ children, sectionName }: { children: ReactNode; sectionName: string }) => {
  return (
    <ErrorBoundary
      level="section"
      componentName={sectionName}
      onError={(error) => {
        console.error('SectionErrorBoundary', `Section "${sectionName}" error:`, error.message);
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

/**
 * PageErrorBoundary
 * Convenient wrapper for page-level errors
 */
export const PageErrorBoundary = ({ children }: { children: ReactNode }) => {
  return (
    <ErrorBoundary
      level="page"
      showDetails={true}
      onError={(error, errorInfo) => {
        console.error('PageErrorBoundary', 'Critical page error:', { error, errorInfo });
      }}
    >
      {children}
    </ErrorBoundary>
  );
};
